import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type {
  BranchDto,
  DailySalesLine,
  DeliveryRiderRow,
  ReportExportColumn,
  ReportExportRequest,
  StaffAttribution,
  StaffSalesRow,
} from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { ExportMenu } from '@/components/ExportMenu';
import { AsyncSection } from '@/components/AsyncSection';
import { ReportFilterBar } from '@/components/ReportFilterBar';
import {
  ReportColumnsButton,
  ReportPeriodButton,
  ReportSearchField,
  ReportToolbar,
} from '@/components/ReportToolbar';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { downloadReportExport, type ExportFormat } from '@/features/reports/api';
import { fetchDailySales, fetchDeliveries, fetchSalesByStaff, dailySalesKeys } from '@/features/reports/daily-sales-api';
import { useReportFilters } from '@/features/reports/useReportFilters';
import type { ReportFilters } from '@/features/reports/useReportFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { useReportToolbar } from '@/features/reports/useReportToolbar';
import { usePortal } from '@/features/portal/PortalContext';

const money = (value: number) => value.toFixed(2);
const qty = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(3).replace(/0+$/, ''));

type Tab = 'daily' | 'staff' | 'delivery';

const TABS: { value: Tab; label: string }[] = [
  { value: 'daily', label: 'Daily summary' },
  { value: 'staff', label: 'Staff' },
  { value: 'delivery', label: 'Delivery' },
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** 'YYYY-MM' → 'August 2026'. */
function monthLabel(ym: string): string {
  const [year, month] = ym.split('-');
  return `${MONTHS[(Number(month) || 1) - 1]} ${year}`;
}

function periodLabel(line: DailySalesLine): string {
  if (line.kind === 'month') return `${monthLabel(line.period)} subtotal`;
  if (line.kind === 'total') return 'Total';
  return line.period;
}

// ── Daily summary ──────────────────────────────────────────────────────────

const DAILY_TOGGLEABLE = [
  'bills', 'items', 'cash', 'card', 'credit', 'other', 'gross', 'returns', 'net',
  'vatPercent', 'taxable', 'vat', 'finalPrice',
] as const;

const DAILY_LABELS: Record<string, string> = {
  bills: 'Bills',
  items: 'Items',
  cash: 'Cash',
  card: 'Card',
  credit: 'Credit',
  other: 'Other tenders',
  gross: 'Gross sales',
  returns: 'Returns',
  net: 'Net sales',
  vatPercent: 'VAT %',
  taxable: 'Taxable amount',
  vat: 'VAT amount',
  finalPrice: 'Final price',
};

const DAILY_COLUMNS: Column<DailySalesLine>[] = [
  { key: 'period', header: 'Date', pinned: true, cell: periodLabel },
  { key: 'bills', header: 'Bills', align: 'right', cell: (row) => String(row.bills) },
  { key: 'items', header: 'Items', align: 'right', cell: (row) => qty(row.items) },
  { key: 'cash', header: 'Cash', align: 'right', cell: (row) => money(row.cash) },
  { key: 'card', header: 'Card', align: 'right', cell: (row) => money(row.card) },
  { key: 'credit', header: 'Credit', align: 'right', cell: (row) => money(row.credit) },
  { key: 'other', header: 'Other tenders', align: 'right', cell: (row) => money(row.other) },
  { key: 'gross', header: 'Gross sales', align: 'right', cell: (row) => money(row.gross) },
  { key: 'returns', header: 'Returns', align: 'right', cell: (row) => money(row.returns) },
  { key: 'net', header: 'Net sales', align: 'right', cell: (row) => money(row.net) },
  { key: 'vatPercent', header: 'VAT %', align: 'right', cell: (row) => `${row.vatPercent.toFixed(2)}%` },
  { key: 'taxable', header: 'Taxable amount', align: 'right', cell: (row) => money(row.taxable) },
  { key: 'vat', header: 'VAT amount', align: 'right', cell: (row) => money(row.vat) },
  {
    key: 'finalPrice',
    header: 'Final price',
    align: 'right',
    cellClassName: 'font-semibold text-slate-900',
    cell: (row) => money(row.finalPrice),
  },
];

/** The totals footer shows the same cells as a row, minus the label column. */
function lineCells(line: DailySalesLine): Record<string, string> {
  const cells: Record<string, string> = {};
  for (const column of DAILY_COLUMNS) {
    if (column.key !== 'period') cells[column.key] = String(column.cell(line));
  }
  return cells;
}

// ── Staff ──────────────────────────────────────────────────────────────────

const STAFF_TOGGLEABLE = ['bills', 'items', 'gross', 'discounts', 'returns', 'net'] as const;
const CASHIER_TOGGLEABLE = [...STAFF_TOGGLEABLE, 'voids', 'voidsValue'] as const;
const STAFF_LABELS: Record<string, string> = {
  bills: 'Bills',
  items: 'Items',
  gross: 'Gross',
  discounts: 'Discounts',
  returns: 'Returns',
  net: 'Net',
  voids: 'Voids',
  voidsValue: 'Voided value',
};

/** The Staff tab's two ways of crediting a bill — the POS's Staff History does both. */
const ATTRIBUTIONS: { value: StaffAttribution; label: string }[] = [
  { value: 'salesPerson', label: 'By sales person' },
  { value: 'cashier', label: 'By who rang the bill' },
];

const unassigned = <span className="italic text-slate-400">No sales person</span>;
// Bills synced before the till sent who rang them up.
const notRecorded = <span className="italic text-slate-400">Not recorded</span>;

const STAFF_VALUE_COLUMNS: Column<StaffSalesRow>[] = [
  { key: 'bills', header: 'Bills', align: 'right', cell: (row) => String(row.bills) },
  { key: 'items', header: 'Items', align: 'right', cell: (row) => qty(row.items) },
  { key: 'gross', header: 'Gross', align: 'right', cell: (row) => money(row.gross) },
  { key: 'discounts', header: 'Discounts', align: 'right', cell: (row) => money(row.discounts) },
  { key: 'returns', header: 'Returns', align: 'right', cell: (row) => money(row.returns) },
  { key: 'net', header: 'Net', align: 'right', cellClassName: 'font-semibold text-slate-900', cell: (row) => money(row.net) },
];

const STAFF_COLUMNS: Column<StaffSalesRow>[] = [
  { key: 'name', header: 'Sales person', pinned: true, cell: (row) => row.name ?? unassigned },
  ...STAFF_VALUE_COLUMNS,
];

const CASHIER_COLUMNS: Column<StaffSalesRow>[] = [
  { key: 'name', header: 'Rang up by', pinned: true, cell: (row) => row.name ?? notRecorded },
  ...STAFF_VALUE_COLUMNS,
  { key: 'voids', header: 'Voids', align: 'right', cell: (row) => String(row.voids) },
  { key: 'voidsValue', header: 'Voided value', align: 'right', cell: (row) => money(row.voidsValue) },
];

// ── Delivery ───────────────────────────────────────────────────────────────

const DELIVERY_TOGGLEABLE = [
  'orders', 'items', 'deliveryCharges', 'orderValue', 'returns', 'net', 'paidOrders', 'unpaidOrders', 'outstanding',
] as const;
const DELIVERY_LABELS: Record<string, string> = {
  orders: 'Orders',
  items: 'Items',
  deliveryCharges: 'Delivery charges',
  orderValue: 'Order value',
  returns: 'Returns',
  net: 'Net',
  paidOrders: 'Paid',
  unpaidOrders: 'Unpaid',
  outstanding: 'Outstanding',
};

const noRider = <span className="italic text-slate-400">Unassigned</span>;

const DELIVERY_COLUMNS: Column<DeliveryRiderRow>[] = [
  { key: 'rider', header: 'Rider', pinned: true, cell: (row) => row.rider ?? noRider },
  { key: 'orders', header: 'Orders', align: 'right', cell: (row) => String(row.orders) },
  { key: 'items', header: 'Items', align: 'right', cell: (row) => qty(row.items) },
  { key: 'deliveryCharges', header: 'Delivery charges', align: 'right', cell: (row) => money(row.deliveryCharges) },
  { key: 'orderValue', header: 'Order value', align: 'right', cell: (row) => money(row.orderValue) },
  { key: 'returns', header: 'Returns', align: 'right', cell: (row) => money(row.returns) },
  { key: 'net', header: 'Net', align: 'right', cell: (row) => money(row.net) },
  { key: 'paidOrders', header: 'Paid', align: 'right', cell: (row) => String(row.paidOrders) },
  { key: 'unpaidOrders', header: 'Unpaid', align: 'right', cell: (row) => String(row.unpaidOrders) },
  {
    key: 'outstanding',
    header: 'Outstanding',
    align: 'right',
    cell: (row) => (
      <span className={row.outstanding > 0 ? 'font-semibold text-amber-700' : undefined}>{money(row.outstanding)}</span>
    ),
  },
];

/** Sums the rows on screen — counts as integers, quantities trimmed, money to 2 places. */
function sumTotals<T>(rows: readonly T[], money2: (keyof T & string)[], counts: (keyof T & string)[]): Record<string, string> {
  const cells: Record<string, string> = {};
  const sum = (key: keyof T & string) => rows.reduce((acc, row) => acc + Number(row[key] ?? 0), 0);
  for (const key of money2) cells[key] = money(sum(key));
  for (const key of counts) cells[key] = qty(sum(key));
  return cells;
}

function matchesSearch(value: string | null, search: string): boolean {
  const term = search.trim().toLowerCase();
  if (term === '') return true;
  return (value ?? '').toLowerCase().includes(term);
}

// ── Export ─────────────────────────────────────────────────────────────────

/** Written to the spreadsheet as currency numbers. */
const EXPORT_MONEY = new Set([
  'cash', 'card', 'credit', 'other', 'gross', 'returns', 'net', 'taxable', 'vat', 'finalPrice',
  'discounts', 'voidsValue', 'deliveryCharges', 'orderValue', 'outstanding',
]);
/** Whole counts. Items stays text: weighed goods make it decimal, and a count format would round it. */
const EXPORT_COUNTS = new Set(['bills', 'voids', 'orders', 'paidOrders', 'unpaidOrders']);

/** '2026-10-01' → '01 Oct 2026', read as a calendar date rather than UTC midnight. */
function readableDate(ymd: string): string {
  const [year, month, day] = ymd.split('-').map(Number);
  if (!year || !month || !day) return ymd;
  return new Date(year, month - 1, day).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** "01 Oct 2026 – 16 Oct 2026 · Main branch · POS" — an empty range is the API's 30-day default. */
function exportSubtitle(filters: ReportFilters, branches: readonly BranchDto[], extra: string[]): string {
  const range =
    filters.from && filters.to ? `${readableDate(filters.from)} – ${readableDate(filters.to)}` : 'Last 30 days';
  const branch = branches.find((option) => option.id === filters.branchId)?.name ?? 'All branches';
  const docType = filters.docType === 'pos' ? 'POS' : filters.docType === 'invoice' ? 'Invoice' : '';
  return [range, branch, docType, ...extra].filter(Boolean).join(' · ');
}

/**
 * One tab's table as an export — the columns the user's layout shows, in its
 * order. `text` overrides the cells that render markup on screen; the
 * first (pinned) column carries the totals label, so `totals` skips it.
 */
function tabTable<T>(
  title: string,
  subtitle: string,
  filters: ReportFilters,
  columns: readonly Column<T>[],
  rows: readonly T[],
  totals: Record<string, string>,
  text: Partial<Record<string, (row: T) => string>> = {},
): ReportExportRequest {
  const exportColumns: ReportExportColumn[] = columns.map((column) => ({
    header: column.header,
    ...(column.align === 'right' ? { align: 'right' as const } : {}),
    ...(EXPORT_MONEY.has(column.key) ? { money: true } : EXPORT_COUNTS.has(column.key) ? { numeric: true } : {}),
  }));
  return {
    title,
    subtitle,
    from: filters.from,
    to: filters.to,
    columns: exportColumns,
    rows: rows.map((row) => columns.map((column) => text[column.key]?.(row) ?? String(column.cell(row)))),
    totals: { label: 'Total', cells: columns.slice(1).map((column) => totals[column.key] ?? '') },
  };
}

export function DailySalesPage() {
  const { profile } = usePortal();
  const [filters, setFilters] = useReportFilters();
  const toolbar = useReportToolbar(filters, setFilters);
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab: Tab = tabParam === 'staff' || tabParam === 'delivery' ? tabParam : 'daily';
  const by: StaffAttribution = params.get('by') === 'cashier' ? 'cashier' : 'salesPerson';
  const setBy = (next: StaffAttribution) =>
    setParams((prev) => {
      const merged = new URLSearchParams(prev);
      if (next === 'salesPerson') merged.delete('by');
      else merged.set('by', next);
      return merged;
    });
  const setTab = (next: Tab) =>
    setParams((prev) => {
      const merged = new URLSearchParams(prev);
      if (next === 'daily') merged.delete('tab');
      else merged.set('tab', next);
      return merged;
    });

  // Search is local, not in the URL filters: these endpoints return their
  // whole result set, and putting it in the query key would refetch per keystroke.
  const [staffSearch, setStaffSearch] = useState('');
  const [riderSearch, setRiderSearch] = useState('');

  const dailyLayout = useReportLayout('daily-sales', DAILY_TOGGLEABLE);
  const staffLayout = useReportLayout('daily-sales-staff', STAFF_TOGGLEABLE);
  const cashierLayout = useReportLayout('daily-sales-cashier', CASHIER_TOGGLEABLE);
  const byCashier = by === 'cashier';
  const activeStaffLayout = byCashier ? cashierLayout : staffLayout;
  const deliveryLayout = useReportLayout('daily-sales-delivery', DELIVERY_TOGGLEABLE);

  const dailyQuery = useQuery({
    queryKey: dailySalesKeys.daily(filters),
    queryFn: ({ signal }) => fetchDailySales(filters, signal),
    enabled: tab === 'daily',
  });
  const staffQuery = useQuery({
    queryKey: dailySalesKeys.byStaff(filters),
    queryFn: ({ signal }) => fetchSalesByStaff(filters, signal),
    enabled: tab === 'staff',
  });
  const deliveryQuery = useQuery({
    queryKey: dailySalesKeys.deliveries(filters),
    queryFn: ({ signal }) => fetchDeliveries(filters, signal),
    enabled: tab === 'delivery',
  });

  // The rows each tab shows, outside AsyncSection too so the toolbar's
  // Export sends exactly them.
  const staffRows = staffQuery.data?.filter((row) => row.attribution === by && matchesSearch(row.name, staffSearch));
  const deliveryRows = deliveryQuery.data?.filter((row) => matchesSearch(row.rider, riderSearch));
  const branches = profile?.branches ?? [];
  const staffColumns = applyReportLayout(byCashier ? CASHIER_COLUMNS : STAFF_COLUMNS, activeStaffLayout);
  const staffTotals = (rows: readonly StaffSalesRow[]) =>
    sumTotals(
      rows,
      byCashier ? ['gross', 'discounts', 'returns', 'net', 'voidsValue'] : ['gross', 'discounts', 'returns', 'net'],
      byCashier ? ['bills', 'items', 'voids'] : ['bills', 'items'],
    );
  const deliveryTotals = (rows: readonly DeliveryRiderRow[]) =>
    sumTotals(
      rows,
      ['deliveryCharges', 'orderValue', 'returns', 'net', 'outstanding'],
      ['orders', 'items', 'paidOrders', 'unpaidOrders'],
    );

  return (
    <div>
      <PageHeader
        title="Daily sales"
        description="Day-by-day takings by tender, sales by staff, and deliveries by rider. Voided bills are left out."
      />
      <ReportFilterBar
        filters={filters}
        onChange={setFilters}
        branches={profile?.branches ?? []}
        showDocType
        hideDateRange
      />

      <div className="mb-4 flex overflow-hidden rounded-lg border border-slate-300 sm:inline-flex" role="tablist" aria-label="Report">
        {TABS.map((option, index) => (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={tab === option.value}
            onClick={() => setTab(option.value)}
            className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
              index > 0 ? 'border-l border-slate-300' : ''
            } ${
              tab === option.value ? 'bg-brand-600/[0.12] text-brand-700' : 'bg-white text-slate-700 hover:bg-slate-100'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {tab === 'daily' ? (
        <section role="tabpanel" aria-label="Daily summary">
          <ReportToolbar>
            <ReportColumnsButton layout={dailyLayout} labels={DAILY_LABELS} />
            <ReportPeriodButton from={filters.from} to={filters.to} onChange={toolbar.setRange} />
            {dailyQuery.data && dailyQuery.data.lines.length > 0 ? (
              <div className="ml-auto">
                <ExportMenu
                  onExport={(format: ExportFormat) =>
                    downloadReportExport(
                      tabTable(
                        'Daily Sales',
                        exportSubtitle(filters, branches, []),
                        filters,
                        applyReportLayout(DAILY_COLUMNS, dailyLayout),
                        dailyQuery.data.lines,
                        lineCells(dailyQuery.data.total),
                      ),
                      format,
                    )
                  }
                />
              </div>
            ) : null}
          </ReportToolbar>
          <AsyncSection
            state={dailyQuery}
            isEmpty={(report) => report.lines.length === 0}
            emptyTitle="No sales in this window"
            skeleton={<TableSkeleton columns={14} />}
          >
            {(report) => (
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <DataTable
                  caption="Daily sales summary"
                  layout={dailyLayout}
                  columns={applyReportLayout(DAILY_COLUMNS, dailyLayout)}
                  rows={report.lines}
                  rowKey={(row) => `${row.kind}-${row.period}`}
                  rowClassName={(row) => (row.kind === 'month' ? 'bg-slate-50 font-semibold' : undefined)}
                  totals={{ label: 'Total', cells: lineCells(report.total) }}
                />
              </div>
            )}
          </AsyncSection>
        </section>
      ) : null}

      {tab === 'staff' ? (
        <section role="tabpanel" aria-label="Staff">
          <div
            className="mb-3 flex overflow-hidden rounded-lg border border-slate-300 sm:inline-flex"
            role="radiogroup"
            aria-label="Credit bills to"
          >
            {ATTRIBUTIONS.map((option, index) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={by === option.value}
                onClick={() => setBy(option.value)}
                className={`flex-1 px-3 py-1.5 text-sm font-medium transition-colors ${
                  index > 0 ? 'border-l border-slate-300' : ''
                } ${
                  by === option.value ? 'bg-brand-600/[0.12] text-brand-700' : 'bg-white text-slate-700 hover:bg-slate-100'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <ReportToolbar>
            <ReportSearchField
              value={staffSearch}
              onChange={setStaffSearch}
              placeholder={byCashier ? 'Search staff login' : 'Search sales person'}
            />
            <ReportColumnsButton layout={activeStaffLayout} labels={STAFF_LABELS} />
            <ReportPeriodButton from={filters.from} to={filters.to} onChange={toolbar.setRange} />
            {staffRows && staffRows.length > 0 ? (
              <div className="ml-auto">
                <ExportMenu
                  onExport={(format: ExportFormat) =>
                    downloadReportExport(
                      tabTable(
                        'Sales by Staff',
                        exportSubtitle(filters, branches, [
                          byCashier ? 'By who rang the bill' : 'By sales person',
                          staffSearch.trim() ? `Matching "${staffSearch.trim()}"` : '',
                        ]),
                        filters,
                        staffColumns,
                        staffRows,
                        staffTotals(staffRows),
                        { name: (row) => row.name ?? (byCashier ? 'Not recorded' : 'No sales person') },
                      ),
                      format,
                    )
                  }
                />
              </div>
            ) : null}
          </ReportToolbar>
          <AsyncSection
            state={staffQuery}
            select={(rows: StaffSalesRow[]) =>
              rows.filter((row) => row.attribution === by && matchesSearch(row.name, staffSearch))
            }
            isEmpty={(rows) => rows.length === 0}
            emptyTitle="No sales in this window"
            skeleton={<TableSkeleton columns={byCashier ? 9 : 7} />}
          >
            {(rows) => (
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <DataTable
                  caption={byCashier ? 'Sales by the staff login that rang them up' : 'Sales by sales person'}
                  layout={activeStaffLayout}
                  columns={staffColumns}
                  rows={rows}
                  rowKey={(row) => row.name ?? '__none__'}
                  totals={{ label: 'Total', cells: staffTotals(rows) }}
                />
              </div>
            )}
          </AsyncSection>
        </section>
      ) : null}

      {tab === 'delivery' ? (
        <section role="tabpanel" aria-label="Delivery">
          <ReportToolbar>
            <ReportSearchField value={riderSearch} onChange={setRiderSearch} placeholder="Search rider" />
            <ReportColumnsButton layout={deliveryLayout} labels={DELIVERY_LABELS} />
            <ReportPeriodButton from={filters.from} to={filters.to} onChange={toolbar.setRange} />
            {deliveryRows && deliveryRows.length > 0 ? (
              <div className="ml-auto">
                <ExportMenu
                  onExport={(format: ExportFormat) =>
                    downloadReportExport(
                      tabTable(
                        'Deliveries by Rider',
                        exportSubtitle(filters, branches, [riderSearch.trim() ? `Matching "${riderSearch.trim()}"` : '']),
                        filters,
                        applyReportLayout(DELIVERY_COLUMNS, deliveryLayout),
                        deliveryRows,
                        deliveryTotals(deliveryRows),
                        { rider: (row) => row.rider ?? 'Unassigned', outstanding: (row) => money(row.outstanding) },
                      ),
                      format,
                    )
                  }
                />
              </div>
            ) : null}
          </ReportToolbar>
          <AsyncSection
            state={deliveryQuery}
            select={(rows: DeliveryRiderRow[]) => rows.filter((row) => matchesSearch(row.rider, riderSearch))}
            isEmpty={(rows) => rows.length === 0}
            emptyTitle="No delivery orders in this window"
            skeleton={<TableSkeleton columns={10} />}
          >
            {(rows) => (
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <DataTable
                  caption="Delivery orders by rider"
                  layout={deliveryLayout}
                  columns={applyReportLayout(DELIVERY_COLUMNS, deliveryLayout)}
                  rows={rows}
                  rowKey={(row) => row.rider ?? '__none__'}
                  totals={{ label: 'Total', cells: deliveryTotals(rows) }}
                />
              </div>
            )}
          </AsyncSection>
        </section>
      ) : null}
    </div>
  );
}
